import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import {
  Home,
  History,
  LogIn,
  LogOut,
  Settings,
  ChevronDown,
  Wallet,
  Gift,
  HelpCircle,
  HeartHandshake,
  Sun,
  Moon,
  Inbox,
  Radio,
  Smartphone,
  Megaphone,
  MessagesSquare,
  Image as ImageIcon,
  Crown,
} from "lucide-react";
import { useState, useRef, useEffect, lazy, Suspense } from "react";

// Lazy để react-easy-crop + form cài đặt không nằm trong bundle khởi động
// (TopNav render trên mọi trang; dialog chỉ cần khi user mở "Thiết lập hồ sơ").
const BoideSettingsDialog = lazy(() =>
  import("@/components/boide-settings-dialog").then((m) => ({ default: m.BoideSettingsDialog })),
);
const SupportChatPopup = lazy(() =>
  import("@/components/support-chat").then((m) => ({ default: m.SupportChat })),
);
import {
  IconAmDuong,
  IconChiTay,
  IconChomSao,
  IconDongXu,
  IconLaBan,
  IconLaSo,
  IconNapTien,
  IconDoanGio,
  IconTuTru,
  IconVanKhi,
} from "@/components/nav-icons";
import { useTheme } from "@/hooks/use-theme";
import { CheckinButton } from "@/components/checkin-button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { logoutBoide } from "@/lib/boide-api";
import { loadInboxUnreadCount } from "@/lib/bootstrap";
import { signOutFirebase } from "@/lib/firebase";
import { useQuery } from "@tanstack/react-query";
import { trackEvent } from "@/lib/analytics";
import { useBoideUser, displayName, initials, clearBoideUser } from "@/hooks/use-boide-user";
import { useTour } from "@/components/tour/tour-provider";
import logoIcon from "@/assets/logo-icon-128.webp";
import { EventNavbar } from "@/components/event";
import { useEventTheme } from "@/hooks/use-event-ui";
import { navbarClipOf } from "@/lib/events/registry";
import { useQuotaStatus } from "@/hooks/use-quota-status";
import { planLabelOf } from "@/components/subscription/quota-row";
import { useIsMobile } from "@/hooks/use-mobile";
import { useReducedEffects } from "@/hooks/use-reduced-effects";

// Gộp hai công cụ lập lá số vào một mục để thanh điều hướng không bị quá tải.
const navItems = [
  { title: "Trang chủ", url: "/", icon: Home },
  { title: "Kinh Dịch", url: "/kinh-dich", icon: IconDongXu },
  {
    title: "Bói Đê",
    url: "/tong-hop",
    icon: IconAmDuong,
    aliases: ["/chu-de"],
    excludes: ["/tong-hop/van-khi", "/tong-hop/la-ban"],
  },
  {
    title: "Vận khí",
    url: "/tong-hop/van-khi",
    icon: IconVanKhi,
    // Se duyên nằm trong dropdown này (không còn mục riêng trên thanh nav), nên
    // alias phải có để mục "Vận khí" vẫn sáng khi đang ở trang se duyên.
    aliases: ["/van-khi", "/tong-hop/la-ban", "/se-duyen"],
    tools: true,
  },
  { title: "Lá số", url: "/tu-vi", icon: IconLaSo, chart: true },
  { title: "Chỉ tay", url: "/boi-chi-tay", icon: IconChiTay },
  { title: "Bói Live", url: "/boi-live", icon: Radio },
];

export function TopNav() {
  const [isMounted, setIsMounted] = useState(false);
  const { theme, setTheme } = useTheme();
  const isMobile = useIsMobile();
  const reducedEffects = useReducedEffects();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const location = useRouterState({ select: (s) => s.location });
  const pathname = location.pathname;
  const isAlmanacPage = ["/xem-ngay", "/van-khi", "/tong-hop/van-khi"].includes(pathname);
  const { user, ready } = useBoideUser();
  const { data: inboxUnread } = useQuery({
    queryKey: ["inbox-unread"],
    queryFn: loadInboxUnreadCount,
    enabled: !!user,
    // Cùng khóa với trang chủ; thiếu staleTime thì navbar (mount sau khi `getMe` về)
    // coi dữ liệu trang chủ vừa lấy là cũ và bắn thêm một request.
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const unreadCount = inboxUnread?.unread ?? 0;
  const { startTour, hasActiveTour } = useTour();
  const navigate = useNavigate();

  const name = isMounted ? displayName(user) : "Quý Khách";
  const ini = isMounted ? initials(name) : "QK";
  const amount = isMounted && typeof user?.amount === "number" ? user.amount : null;
  const amountLabel = amount === null ? "—" : `${amount.toLocaleString("vi-VN")}đ`;
  const isUserReady = isMounted && ready;

  // Quota gói ngày, hiện NGAY TRONG viên số dư ở navbar. Dùng chung khóa query với
  // thẻ ví trang chủ nên không thêm request nào.
  //
  // 🔴 Chỉ hiện khi THẬT SỰ có gói đang chạy: viên này rộng 8.5rem trên mobile, một
  // dòng "Gói 0đ" vừa chiếm chỗ vừa nói sai với người chưa mua gói. Khi chưa có dữ
  // liệu (đang tải / BE chưa deploy) cũng không hiện gì — xem `QuotaRow`.
  const quota = useQuotaStatus({ enabled: !!user });
  const quotaPlan = planLabelOf(quota.data?.subscriptions ?? []);
  // Dạng CÒN LẠI / TRẦN NGÀY (vd "138.000/200.000đ"): một con số trần không nói
  // được là nhiều hay ít. Chia cho `quota_limit` chứ không phải `daily_quota` —
  // dòng sổ hôm nay có thể giữ trần cao hơn tổng các đoạn hiện tại.
  const quotaLabel =
    quota.data && quotaPlan.primary
      ? `${Math.max(0, Math.round(quota.data.remaining)).toLocaleString("vi-VN")}/${Math.max(
          0,
          Math.round(quota.data.quota_limit),
        ).toLocaleString("vi-VN")}đ`
      : "";

  // Banner 2/9 cho thanh điều hướng. Ba điều kiện, mỗi cái một lý do riêng:
  //  - `isMobile`: chặn ở khâu RENDER chứ không phải `md:hidden`. Ẩn bằng CSS thì thẻ
  //    <video> vẫn nằm trong DOM và trình duyệt vẫn tải đủ ~227KB cho màn desktop
  //    không bao giờ thấy nó.
  //  - `pathname !== "/"`: trang chủ cố ý KHÔNG đụng tới — ở "/" header đã
  //    `hidden md:block` nên trên mobile không có thanh nào để gắn.
  //  - `isMounted`: `isMobile` chỉ đo được ở client — render server luôn cho false,
  //    nên phải chờ mount xong mới quyết, tránh lệch giữa SSR và hydrate.
  const eventTheme = useEventTheme();
  // Lễ chưa làm banner thì `navbarClipOf` trả null và thanh giữ nguyên dáng thường —
  // 92px cùng một dải artwork chỉ dành cho lễ CÓ clip, chứ không phải cho mọi lễ.
  const eventClip = eventTheme ? navbarClipOf(eventTheme) : null;
  const showEventNavbar = isMounted && isMobile && eventClip !== null && pathname !== "/";

  const [open, setOpen] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  const chartMenuRef = useRef<HTMLDivElement>(null);
  const chartMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsMenuRef = useRef<HTMLDivElement>(null);
  const toolsMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  // Chỉ mount dialog (và tải chunk lazy) từ lần mở đầu tiên; giữ mount sau đó
  // để animation đóng của dialog không bị cắt.
  const [settingsMounted, setSettingsMounted] = useState(false);

  // Nút hỗ trợ nổi trên mobile — cùng cơ chế kéo thả với tour guide.
  const supportButtonRef = useRef<HTMLButtonElement>(null);
  const [supportDragPos, setSupportDragPos] = useState({ x: 0, y: 0 });
  const supportDragStart = useRef<{
    x: number;
    y: number;
    posX: number;
    posY: number;
  } | null>(null);
  const supportHasDragged = useRef(false);

  const handleSupportPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    supportDragStart.current = {
      x: e.clientX,
      y: e.clientY,
      posX: supportDragPos.x,
      posY: supportDragPos.y,
    };
    supportHasDragged.current = false;
  };

  const handleSupportPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!supportDragStart.current) return;
    const dx = e.clientX - supportDragStart.current.x;
    const dy = e.clientY - supportDragStart.current.y;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) supportHasDragged.current = true;
    if (supportButtonRef.current) {
      supportButtonRef.current.style.transform = `translate3d(${supportDragStart.current.posX + dx}px, ${supportDragStart.current.posY + dy}px, 0)`;
    }
  };

  const handleSupportPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!supportDragStart.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const dx = e.clientX - supportDragStart.current.x;
    const dy = e.clientY - supportDragStart.current.y;
    setSupportDragPos({
      x: supportDragStart.current.posX + dx,
      y: supportDragStart.current.posY + dy,
    });
    supportDragStart.current = null;
  };

  const handleSupportClick = (e: React.MouseEvent) => {
    if (supportHasDragged.current) {
      e.preventDefault();
      e.stopPropagation();
      supportHasDragged.current = false;
      return;
    }
    setSupportOpen(true);
  };

  useEffect(() => {
    if (!supportOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSupportOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [supportOpen]);

  // Drag-and-drop state for mobile floating tour guide bubble
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const dragStart = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);
  const hasDragged = useRef(false);

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      posX: dragPos.x,
      posY: dragPos.y,
    };
    hasDragged.current = false;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasDragged.current = true;
    }
    const newX = dragStart.current.posX + dx;
    const newY = dragStart.current.posY + dy;
    if (buttonRef.current) {
      buttonRef.current.style.transform = `translate3d(${newX}px, ${newY}px, 0)`;
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStart.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setDragPos({
      x: dragStart.current.posX + dx,
      y: dragStart.current.posY + dy,
    });
    dragStart.current = null;
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    if (hasDragged.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDragged.current = false;
      return;
    }
    startTour();
  };
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
      if (chartMenuRef.current && !chartMenuRef.current.contains(e.target as Node)) {
        setChartOpen(false);
      }
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleMenuEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      userMenuTriggerRef.current?.focus();
    };
    document.addEventListener("keydown", handleMenuEscape);
    return () => document.removeEventListener("keydown", handleMenuEscape);
  }, [open]);

  useEffect(() => {
    if (!chartOpen) return;
    chartMenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const handleChartMenuEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setChartOpen(false);
      chartMenuTriggerRef.current?.focus();
    };
    document.addEventListener("keydown", handleChartMenuEscape);
    return () => document.removeEventListener("keydown", handleChartMenuEscape);
  }, [chartOpen]);

  useEffect(() => {
    if (!toolsOpen) return;
    toolsMenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const handleToolsMenuEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setToolsOpen(false);
      toolsMenuTriggerRef.current?.focus();
    };
    document.addEventListener("keydown", handleToolsMenuEscape);
    return () => document.removeEventListener("keydown", handleToolsMenuEscape);
  }, [toolsOpen]);

  const handleLogout = async () => {
    trackEvent("logout", { source: "top_nav" });
    logoutBoide();
    clearBoideUser();
    await signOutFirebase();
    toast.success("Đã thoát khỏi cõi giới.");
    navigate({ to: "/login" });
    setOpen(false);
  };

  return (
    <>
      <header
        className={`sticky top-0 z-40 w-full border-b border-gold-soft/45 bg-background/95 backdrop-blur-xl ${
          pathname === "/" ? "hidden md:block" : ""
        }`}
        // Trong PWA standalone, iOS đặt status bar chồng lên vùng đầu trang.
        // Chừa safe-area ở chính header để logo, số dư và các nút không bị đồng hồ
        // / cụm sóng pin phủ lên trên (đặc biệt ở các route không phải trang chủ).
        style={{ paddingTop: "var(--safe-top)" }}
      >
        {/* Lớp nền video nằm TRƯỚC nội dung và là con absolute duy nhất của header.
            Không cần thêm `relative`: `sticky` đã là position dương nên header đã là
            containing block — thêm `relative` là hai utility cùng ghi `position`, ai
            thắng do thứ tự trong CSS quyết chứ không do thứ tự viết trong className. */}
        {showEventNavbar && eventClip ? (
          <EventNavbar clip={eventClip} staticOnly={reducedEffects} />
        ) : null}

        {/* `relative z-10`: con absolute vẽ đè lên anh em TĨNH, thiếu dòng này thì
            logo, hộp thư và số dư nằm dưới video. */}
        {/* Thanh cao hơn KHI CÓ banner sự kiện: 5,75rem (92px). Dải banner trải hết bề
            ngang nên tỉ lệ clip (3,60:1) quyết chiều cao "vừa khít" — ở máy 375px là
            ~104px. Hạ xuống 92px là cố ý nhận phần bị `cover` cắt (~12%), và vì thế
            `object-position` phải neo ĐỈNH (xem styles.css): cắt từ dưới lên thì chỉ
            mất bánh xe tăng, neo giữa là cắt ngang ngọn số "2·9". Chỉ nới ở mobile và
            chỉ khi banner bật: `md:h-20` giữ nguyên thanh desktop, ngày thường vẫn
            56px để không đẩy lệch layout của mọi trang. */}
        <div
          className={`relative z-10 mx-auto flex max-w-[1400px] items-center justify-between gap-2 px-3 md:h-20 md:gap-3 md:px-6 xl:px-8 ${
            showEventNavbar ? "h-[5.75rem]" : "h-14"
          }`}
        >
          {/* Left: Logo — ẨN hẳn khi có banner sự kiện: artwork đã trải kín mép trái,
              logo đặt lên đó chỉ chồng đúng cụm số "2·9". Banner chỉ bật ở mobile nên
              nhánh này không chạm tới thanh desktop. Cụm phải có `ml-auto` để vẫn dạt
              về mép phải khi đây là flex item duy nhất còn lại. */}
          {showEventNavbar ? null : (
            <div className="flex min-w-0 shrink-0 justify-start relative">
              {/* Chữ "BÓI ĐÊ" chỉ hiện từ 2xl — nhường chỗ cho 7 mục menu có nhãn ở xl */}
              <Link to="/" className="flex items-center gap-1 shrink-0 group">
                <div className="flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 group-hover:scale-105 sm:h-10 sm:w-10 md:h-14 md:w-14">
                  <img
                    src={logoIcon}
                    alt="Biểu tượng Bói Đê với hoa sen vàng"
                    className="h-full w-full object-contain filter drop-shadow-[0_0_8px_rgba(212,163,89,0.35)] transition-all duration-300 group-hover:drop-shadow-[0_0_15px_rgba(212,163,89,0.6)]"
                  />
                </div>
                <span
                  className="hidden 2xl:block text-2xl tracking-[-0.02em] text-gold-gradient group-hover:brightness-110 transition-all duration-300 select-none ml-[-8px]"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}
                >
                  BÓI ĐÊ
                </span>
              </Link>
            </div>
          )}

          {/* Center: Nav links — chỉ icon ở md/lg (chật), lộ chữ từ xl trở lên (đủ chỗ) */}
          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 overflow-visible lg:gap-1 md:flex">
            {navItems.map((item) => {
              const excluded = item.excludes?.some((path) => pathname.startsWith(path)) ?? false;
              const active =
                !excluded &&
                (pathname === item.url ||
                  (item.url !== "/" && pathname.startsWith(item.url)) ||
                  item.aliases?.some((path) => pathname.startsWith(path)) ||
                  (item.chart &&
                    (pathname.startsWith("/tu-vi") ||
                      pathname.startsWith("/bat-tu") ||
                      pathname.startsWith("/doan-gio-sinh"))));
              if (item.tools) {
                return (
                  <div key={item.url} ref={toolsMenuRef} className="relative shrink-0">
                    <button
                      ref={toolsMenuTriggerRef}
                      type="button"
                      onClick={() => {
                        setToolsOpen((value) => !value);
                        setChartOpen(false);
                        setOpen(false);
                      }}
                      aria-label="Chọn công cụ vận khí và phong thủy"
                      aria-current={active ? "page" : undefined}
                      aria-expanded={toolsOpen}
                      aria-haspopup="menu"
                      aria-controls="fortune-tools-menu"
                      className={`group flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1.5 text-sm font-medium transition-all duration-200 xl:px-2.5 2xl:px-3 ${active ? "bg-gold-gradient text-primary-foreground shadow-gold" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"}`}
                    >
                      <item.icon className="h-4 w-4 shrink-0 xl:hidden" />
                      <span className="hidden whitespace-nowrap xl:inline">{item.title}</span>
                      <ChevronDown
                        className={`hidden h-3 w-3 transition-transform xl:inline ${toolsOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {toolsOpen ? (
                      <div
                        id="fortune-tools-menu"
                        role="menu"
                        aria-label="Công cụ vận khí, phong thủy và se duyên"
                        className="absolute left-1/2 top-full z-50 mt-2 w-52 -translate-x-1/2 rounded-2xl border border-gold-soft/40 bg-popover p-1.5 shadow-mystic"
                      >
                        <Link
                          to="/tong-hop/van-khi"
                          role="menuitem"
                          onClick={() => setToolsOpen(false)}
                          className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <IconVanKhi className="h-4 w-4 text-primary" />
                          Vận khí hôm nay
                        </Link>
                        <Link
                          to="/tong-hop/la-ban"
                          role="menuitem"
                          onClick={() => setToolsOpen(false)}
                          className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <IconLaBan className="h-4 w-4 text-primary" />
                          La Bàn phong thủy
                        </Link>
                        <Link
                          to="/se-duyen"
                          role="menuitem"
                          onClick={() => setToolsOpen(false)}
                          className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <HeartHandshake className="h-4 w-4 text-primary" />
                          Se duyên
                        </Link>
                      </div>
                    ) : null}
                  </div>
                );
              }
              if (item.chart) {
                return (
                  <div key={item.url} ref={chartMenuRef} className="relative shrink-0">
                    <button
                      ref={chartMenuTriggerRef}
                      type="button"
                      onClick={() => {
                        setChartOpen((value) => !value);
                        setToolsOpen(false);
                        setOpen(false);
                      }}
                      aria-label="Chọn loại lá số"
                      aria-current={active ? "page" : undefined}
                      aria-expanded={chartOpen}
                      aria-haspopup="menu"
                      aria-controls="chart-type-menu"
                      className={`group flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1.5 text-sm font-medium transition-all duration-200 xl:px-2.5 2xl:px-3 ${active ? "bg-gold-gradient text-primary-foreground shadow-gold" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"}`}
                    >
                      <item.icon className="h-4 w-4 shrink-0 xl:hidden" />
                      <span className="hidden whitespace-nowrap xl:inline">{item.title}</span>
                      <ChevronDown
                        className={`hidden h-3 w-3 transition-transform xl:inline ${chartOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {chartOpen ? (
                      <div
                        id="chart-type-menu"
                        role="menu"
                        aria-label="Chọn loại lá số"
                        className="absolute left-1/2 top-full z-50 mt-2 w-48 -translate-x-1/2 rounded-2xl border border-gold-soft/40 bg-popover p-1.5 shadow-mystic"
                      >
                        <Link
                          to="/tu-vi"
                          role="menuitem"
                          onClick={() => setChartOpen(false)}
                          className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <IconChomSao className="h-3.5 w-3.5 text-primary" /> Lá số Tử Vi
                        </Link>
                        <Link
                          to="/bat-tu"
                          role="menuitem"
                          onClick={() => setChartOpen(false)}
                          className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <IconTuTru className="h-3.5 w-3.5 text-primary" /> Lá số Bát Tự
                        </Link>
                        <Link
                          to="/doan-gio-sinh"
                          role="menuitem"
                          onClick={() => setChartOpen(false)}
                          className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <IconDoanGio className="h-3.5 w-3.5 text-primary" /> Đoán giờ sinh
                        </Link>
                      </div>
                    ) : null}
                  </div>
                );
              }
              return (
                <Link
                  key={item.url}
                  to={item.url}
                  aria-label={item.title}
                  aria-current={active ? "page" : undefined}
                  className={`group flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1.5 text-sm font-medium transition-all duration-200 xl:px-2.5 2xl:px-3 whitespace-nowrap ${
                    active
                      ? "bg-gold-gradient text-primary-foreground shadow-gold"
                      : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                  }`}
                >
                  {/* Dưới xl: chỉ icon (gọn). Từ xl: chỉ chữ — bỏ icon để 7 mục + cụm phải
                      logged-in (ví, avatar, điểm danh...) không bao giờ tràn hàng. */}
                  <item.icon className="h-4 w-4 shrink-0 xl:hidden" />
                  <span className="hidden xl:inline whitespace-nowrap">{item.title}</span>
                </Link>
              );
            })}
          </nav>

          {/* Right side — user & utils (cùng chung hàng chính, gap gọn để nhường chỗ menu) */}
          <div className="ml-auto flex shrink-0 items-center justify-end gap-1.5 md:gap-2">
            {isAlmanacPage ? (
              <button
                type="button"
                onClick={() => setSupportOpen(true)}
                aria-label="Trò chuyện với hỗ trợ Bói Đê"
                title="Hỗ trợ trực tuyến"
                className="flex size-10 shrink-0 items-center justify-center rounded-full border border-gold-soft/50 bg-card text-primary transition hover:bg-accent md:hidden"
              >
                <MessagesSquare className="size-4" />
              </button>
            ) : null}
            <Link
              to="/ho-tro"
              aria-label="Trò chuyện với hỗ trợ Bói Đê"
              title="Hỗ trợ trực tuyến"
              className={`hidden h-9 w-9 shrink-0 items-center justify-center rounded-full border transition active:scale-95 md:flex ${
                pathname === "/ho-tro"
                  ? "border-primary bg-gold-gradient text-primary-foreground shadow-gold"
                  : "border-gold-soft/50 bg-card/50 text-primary/80 hover:border-primary/60 hover:text-primary"
              }`}
            >
              <MessagesSquare className="h-4 w-4" />
            </Link>
            {hasActiveTour && (
              <button
                type="button"
                data-tour="tour-help"
                onClick={startTour}
                aria-label="Xem hướng dẫn trang này"
                title="Xem hướng dẫn trang này"
                className="hidden md:flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold-soft/50 bg-card/50 text-primary/80 transition hover:border-primary/60 hover:text-primary"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            )}

            {isMounted && (
              <button
                type="button"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label="Chuyển giao diện Sáng/Tối"
                title="Chuyển giao diện Sáng/Tối"
                className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold-soft/50 bg-card/50 text-primary/80 transition hover:border-primary/60 hover:text-primary active:scale-95 md:flex"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            )}

            <div className="hidden md:block">
              <CheckinButton />
            </div>

            <div className="hidden h-5 w-px shrink-0 bg-border/40 md:mx-1 md:block" />

            {!isUserReady ? (
              <div className="h-8 w-8 md:h-9 md:w-9 animate-pulse rounded-full bg-muted/30" />
            ) : !user ? (
              <Link
                to="/login"
                search={pathname === "/" ? {} : { returnTo: location.href }}
                aria-label="Đăng nhập"
                className={`flex items-center gap-1.5 rounded-full border border-gold-soft px-3 py-1 text-xs font-medium text-primary transition hover:border-primary/60 md:px-4 md:py-1.5 md:text-sm ${
                  showEventNavbar ? "bg-card/95 backdrop-blur-sm shadow-sm" : "bg-card/60"
                }`}
              >
                <LogIn className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Khai môn</span>
              </Link>
            ) : (
              <div className="flex items-center gap-1.5 md:gap-2">
                <Link
                  to="/hop-thu"
                  title="Hộp thư"
                  className={`relative flex items-center justify-center rounded-full border border-gold-soft/50 text-primary transition hover:border-primary/60 hover:shadow-gold ${
                    showEventNavbar
                      ? "p-2.5 bg-card/95 backdrop-blur-sm shadow-sm hover:bg-card"
                      : "p-1.5 sm:p-2 bg-card/50 hover:bg-card/70"
                  }`}
                >
                  <Inbox className={showEventNavbar ? "h-5 w-5" : "h-3.5 w-3.5 sm:h-4 sm:w-4"} />
                  {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-gold-gradient px-1 text-[9px] font-bold text-primary-foreground">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/thanh-toan"
                  /* ⚠️ KHÔNG ghép "Gói" + tên gói: tên trong DB đã là "Gói Lớn" nên
                     ghép ra "Gói Gói Lớn". */
                  title={
                    quotaLabel
                      ? `Nạp tiền · Số dư ${amountLabel} · ${quotaPlan.primary?.plan_name ?? "Gói"} còn ${quotaLabel} hôm nay`
                      : `Nạp tiền · Số dư ${amountLabel}`
                  }
                  aria-label={
                    quotaLabel
                      ? `Nạp tiền. Số dư hiện tại ${amountLabel}. ${quotaPlan.primary?.plan_name ?? "Gói"} còn ${quotaLabel} hạn mức hôm nay`
                      : `Nạp tiền. Số dư hiện tại ${amountLabel}`
                  }
                  /* ⚠️ `max-w-[10.5rem]` (không phải 8.5rem như trước): dòng quota
                     "138.000/200.000đ" cần ~101px cộng biểu tượng và padding, ở mức cũ
                     truncate ăn mất phần trần ngày — đo trên máy ở 375px và 320px.
                     Đây là TRẦN, không phải bề rộng: viên vẫn co theo nội dung. Đo lại
                     nếu đổi định dạng số.
                     🔴 Nền `bg-card/70` mặc định để artwork lọt qua sau chữ số dư —
                     trên nền pháo hoa + phố phường thì "93.185.771đ" mất nét hẳn. Khi
                     có banner thì đổi sang gần đục + blur, KHÔNG chồng thêm class thứ
                     hai: hai utility `bg-card/*` cùng lúc thì thứ tự trong CSS quyết,
                     không phải thứ tự viết ở đây. */
                  className={`flex min-w-0 max-w-[10.5rem] items-center gap-1.5 rounded-full border border-gold-soft/60 px-2 text-primary shadow-[0_3px_10px_rgba(113,76,25,0.06)] transition hover:border-primary/60 hover:bg-card hover:shadow-gold sm:max-w-none sm:px-3 ${
                    showEventNavbar
                      ? "h-10 bg-card/95 backdrop-blur-sm shadow-sm"
                      : "h-9 bg-card/70"
                  }`}
                >
                  <IconNapTien className="size-3.5 shrink-0 sm:size-4" />
                  {/* KHÔNG có nhãn "Số dư": biểu tượng ví + con số đã đủ nghĩa, và
                      viên tròn chỉ cao 36–40px nên dòng nhãn ăn mất chỗ của dòng quota.
                      Nghĩa đầy đủ vẫn nằm ở `title`/`aria-label` cho trình đọc màn hình. */}
                  <span className="flex min-w-0 flex-col items-start justify-center">
                    <span className="max-w-full truncate text-[11px] font-bold leading-3.5 tabular-nums sm:text-xs">
                      {amountLabel}
                    </span>
                    {/* Quota gói — CHUNG KHUNG với số dư vì `spendable = quota còn lại
                        + ví`; hai con số ở hai chỗ là để người có gói nhìn số dư rồi
                        tưởng đó là tất cả những gì họ tiêu được.
                        ⚠️ Ba dòng phải vừa trong `h-9` (36px): 10 + 14 + 10 = 34px.
                        Nống `leading` của dòng nào lên là viên tròn bị tràn chữ. */}
                    {quotaLabel ? (
                      <span className="flex max-w-full items-center gap-0.5 truncate text-[8px] leading-2.5 text-muted-foreground">
                        {/* CHỈ CON SỐ — không nhãn, không tên gói, ở MỌI bề rộng.
                            Dạng "còn/trần" tự nó đã phân biệt với dòng số dư ngay trên,
                            và viên tròn này là chỗ hẹp nhất trong app: mỗi ký tự thừa
                            là một ký tự bị truncate cắt. Tên gói đầy đủ vẫn nằm ở
                            `title`/`aria-label`, và hiện rõ ở thẻ ví trang chủ. */}
                        <span className="shrink-0 font-semibold tabular-nums text-primary/90">
                          {quotaLabel}
                        </span>
                      </span>
                    ) : null}
                  </span>
                </Link>
                <div className="relative hidden md:block" ref={dropdownRef}>
                  <button
                    ref={userMenuTriggerRef}
                    id="user-menu-trigger"
                    type="button"
                    onClick={() => {
                      setOpen((value) => !value);
                      setChartOpen(false);
                      setToolsOpen(false);
                    }}
                    aria-label="Mở menu tài khoản"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-controls="user-dropdown-menu"
                    className="flex items-center gap-1 rounded-full border border-gold-soft/60 bg-card/60 pl-1 pr-2 py-1 transition hover:border-primary/60 hover:shadow-gold md:pl-1.5 md:pr-3 md:py-1.5"
                  >
                    <Avatar className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 shrink-0">
                      {user.avatar ? <AvatarImage src={user.avatar} alt={name} /> : null}
                      <AvatarFallback className="bg-gold-gradient text-[10px] font-semibold text-primary-foreground">
                        {ini}
                      </AvatarFallback>
                    </Avatar>
                    {/* Tên chỉ hiện ở màn rất rộng — avatar là đủ nhận diện, nhường chỗ cho menu */}
                    <span className="hidden 2xl:block font-serif-soft text-xs md:text-sm text-foreground max-w-[100px] truncate">
                      {name}
                    </span>
                    <ChevronDown
                      className={`h-3 w-3 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                    />
                  </button>

                  {/* Dropdown */}
                  {open && (
                    <div
                      id="user-dropdown-menu"
                      role="menu"
                      aria-labelledby="user-menu-trigger"
                      className="absolute right-0 top-full mt-2 w-52 sm:w-56 rounded-2xl border border-gold-soft/40 bg-popover shadow-mystic backdrop-blur-xl overflow-hidden animate-mystic-rise z-50"
                    >
                      {/* User info */}
                      <div className="flex items-center gap-2 sm:gap-3 border-b border-border/40 px-3 py-2 sm:px-4 sm:py-3">
                        <Avatar className="h-8 w-8 shrink-0">
                          {user.avatar ? <AvatarImage src={user.avatar} alt={name} /> : null}
                          <AvatarFallback className="bg-gold-gradient text-xs font-semibold text-primary-foreground">
                            {ini}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="truncate text-xs sm:text-sm font-medium text-foreground">
                            {name}
                          </div>
                          <div className="truncate text-[9px] sm:text-[10px] text-muted-foreground">
                            {user.email || ""}
                          </div>
                        </div>
                      </div>

                      {/* Menu items */}
                      <div className="py-1">
                        <Link
                          to="/thanh-toan"
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <Wallet className="h-3.5 w-3.5 text-muted-foreground" />
                          Nạp tiền
                        </Link>
                        <Link
                          to="/goi"
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <Crown className="h-3.5 w-3.5 text-muted-foreground" />
                          Gói quota ngày
                        </Link>
                        <Link
                          to="/hop-thu"
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
                          Hộp thư
                          {unreadCount > 0 && (
                            <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gold-gradient px-1.5 text-[10px] font-bold text-primary-foreground">
                              {unreadCount}
                            </span>
                          )}
                        </Link>
                        <Link
                          to="/lich-su"
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <History className="h-3.5 w-3.5 text-muted-foreground" />
                          Lịch sử
                        </Link>
                        <Link
                          to="/anh-nen"
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
                          Ảnh nền của tôi
                        </Link>
                        <Link
                          to="/gioi-thieu"
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <Gift className="h-3.5 w-3.5 text-muted-foreground" />
                          Giới thiệu & hoa hồng
                        </Link>
                        <Link
                          to="/cam-nhan"
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <Megaphone className="h-3.5 w-3.5 text-muted-foreground" />
                          Chia sẻ nhận 100k
                        </Link>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setSettingsMounted(true);
                            setSettingsOpen(true);
                            setOpen(false);
                          }}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <Settings className="h-3.5 w-3.5 text-muted-foreground" />
                          Thiết lập hồ sơ
                        </button>
                        <Link
                          to="/user-manual"
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
                        >
                          <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                          Hướng dẫn cài đặt app
                        </Link>
                      </div>

                      <div className="border-t border-border/40 py-1">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={handleLogout}
                          className="flex w-full items-center gap-3 px-4 py-2 text-xs sm:text-sm text-destructive/90 transition hover:bg-destructive/10 hover:text-destructive"
                        >
                          <LogOut className="h-3.5 w-3.5" />
                          Đăng xuất
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Settings dialog triggered from dropdown */}
        {settingsMounted && (
          <Suspense fallback={null}>
            <BoideSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
          </Suspense>
        )}
      </header>

      {supportOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Trò chuyện với hỗ trợ Bói Đê"
          className="fixed inset-0 z-[80] flex bg-background/80 p-2 backdrop-blur-md md:hidden"
        >
          <div className="flex min-h-0 w-full flex-1 overflow-hidden rounded-[24px] shadow-2xl">
            <Suspense
              fallback={
                <div className="flex flex-1 items-center justify-center bg-card text-sm text-muted-foreground">
                  Đang mở trợ lý…
                </div>
              }
            >
              <SupportChatPopup onClose={() => setSupportOpen(false)} />
            </Suspense>
          </div>
        </div>
      ) : null}

      {/* `data-floating-fab`: nút nổi phải ẩn khi có modal mở — nó là
          `position: fixed` z-[60] nên nằm ĐÈ lên footer dialog. Quy tắc ở
          styles.css (`body[data-scroll-locked]`).

          Offset `+5.5rem` KHÔNG phải số cho đẹp: composer chat (`iching-result-dock`)
          đo được CAO 79px và nằm ngay trên bottom nav, tức chiếm dải
          `--bottom-nav-h` → `+79px`. Ở mức `+1rem` cũ, nút phủ đúng nút GỬI của
          composer — bấm gửi lại mở chat hỗ trợ. `+5.5rem` (88px) đưa đáy nút lên
          trên composer 9px. Nút hướng dẫn (đang tắt) xếp tiếp ở `+9rem` để hai
          nút không chồng nhau nếu bật lại. */}
      {!supportOpen && !isAlmanacPage && (
        <button
          ref={supportButtonRef}
          type="button"
          onPointerDown={handleSupportPointerDown}
          onPointerMove={handleSupportPointerMove}
          onPointerUp={handleSupportPointerUp}
          onClick={handleSupportClick}
          aria-label="Trò chuyện với hỗ trợ Bói Đê"
          title="Hỗ trợ trực tuyến"
          data-floating-fab
          className="fixed bottom-[calc(var(--bottom-nav-h)+5.5rem)] right-3 z-[60] inline-flex h-11 w-11 items-center justify-center rounded-full border border-primary/50 bg-gold-gradient p-0 text-primary-foreground shadow-[0_10px_30px_rgba(0,0,0,0.22)] transition-colors duration-200 hover:brightness-105 md:hidden active:scale-95"
          style={{
            transform: `translate3d(${supportDragPos.x}px, ${supportDragPos.y}px, 0)`,
            touchAction: "none",
          }}
        >
          <MessagesSquare className="h-4.5 w-4.5" />
        </button>
      )}

      {hasActiveTour && pathname !== "/" && !pathname.startsWith("/tong-hop") && (
        <button
          ref={buttonRef}
          type="button"
          data-tour="tour-help-mobile"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onClick={handleButtonClick}
          aria-label="Xem hướng dẫn trang này"
          title="Xem hướng dẫn trang này"
          className="fixed bottom-[calc(var(--bottom-nav-h)+9rem)] right-3 z-[60] inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold-soft/60 bg-card/90 p-0 text-primary shadow-[0_10px_30px_rgba(0,0,0,0.25)] backdrop-blur-xl transition-colors duration-200 hover:border-primary/60 md:hidden active:scale-95"
          style={{
            transform: `translate3d(${dragPos.x}px, ${dragPos.y}px, 0)`,
            touchAction: "none",
          }}
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      )}
    </>
  );
}
